/*
 * <fleet-server-panel> — the right-side drawer for a Vitesse Private Server (a cloud hub
 * we own the hardware for), opened by clicking its map marker. The server twin of
 * <fleet-gateway-panel>: SAME drawer chrome + SAME pill tabs + the SHARED dial-row /
 * field-grid parts (panel-parts) so every tab is exactly 3 dials + 4 fields, no layout
 * shift. Metrics that aren't wired yet render the dial's pending "—" / a DASH field — the
 * structure is forced, the data fills in as we wire each source.
 *
 * Stats only exist for a super-admin viewer (CloudServer.hasStats); an org-admin sees the
 * node + location + the restricted note. The app keeps `server` live off the FleetControl
 * stream so the dials track the ~1 Hz refresh.
 *
 * Light DOM (createRenderRoot → this) so the shared base-ui SCSS + tokens style it.
 */
import { LitElement, html, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import '@vitesse/base-ui/elements/vitesse-icon';
import { iconButton, statusBadge, pillTabBar, type PillTab, LocaleController } from '@vitesse/base-ui';
import { tabBody, fmtGb, fmtCount, fmtCompact, loadPct } from './panel-parts';
import { ft } from '../core/i18n';
import { DASH, formatUptime } from '../core/fleet-model';
import type { CloudServer } from '../core/fleet-client';

type Tab = 'overview' | 'hardware' | 'software';

@customElement('fleet-server-panel')
export class FleetServerPanel extends LitElement {
  /** The server to show; null → closed (renders nothing). The app keeps it live. */
  @property({ attribute: false }) server: CloudServer | null = null;

  @state() private tab: Tab = 'overview';
  /** Drives the slide-in (false on mount → true next frame → CSS transform transition). */
  @state() private opened = false;
  /** id we last (re)initialised for — a live refresh for the SAME server must not re-slide. */
  private lastId = '';

  constructor() {
    super();
    new LocaleController(this); // re-render translated text on language switch
  }

  protected createRenderRoot(): HTMLElement {
    return this;
  }

  // Same tab set + icons as the gateway panel (folder-kanban / microchip / card-sim).
  private get tabItems(): PillTab[] {
    return [
      { id: 'overview', label: ft('tab.overview'), icon: 'folder-kanban' },
      { id: 'hardware', label: ft('tab.hardware'), icon: 'microchip' },
      { id: 'software', label: ft('tab.software'), icon: 'card-sim' },
    ];
  }

  updated(changed: Map<string, unknown>): void {
    const s = this.server;
    if (changed.has('server') && s && s.id !== this.lastId) {
      this.lastId = s.id;
      this.tab = 'overview';
      this.opened = false;
      requestAnimationFrame(() => (this.opened = true));
    }
  }

  private close(): void {
    this.dispatchEvent(new CustomEvent('panel-close', { bubbles: true, composed: true }));
  }

  private pct(used: number, total: number): number | null {
    return total > 0 ? Math.round((used / total) * 100) : null;
  }

  private renderTab(s: CloudServer): TemplateResult {
    const memPct = this.pct(s.memUsedGb, s.memTotalGb);
    switch (this.tab) {
      // OVERVIEW — vital signs (CPU/Mem) + the headline fleet metric: how many gateways are
      // bound + online right now (activity — high isn't bad). Error-rate is ~0 → a text field
      // (reads better than a perpetually-empty dial).
      case 'overview':
        return tabBody(
          [
            { value: s.cpuPct || null, label: ft('ring.cpu') },
            { value: memPct, label: ft('ring.mem') },
            { value: s.activeConnections || null, label: ft('m.activeConns'), max: 500, unit: '', tone: 'activity' },
          ],
          [
            { label: ft('server.location'), value: s.geo || DASH },
            { label: ft('server.model'), value: s.cpuModel || DASH },
            { label: ft('server.cores'), value: fmtCount(s.cpuCores) },
            { label: ft('server.uptime'), value: formatUptime(s.uptimeS) },
            { label: ft('m.errorRate'), value: `${s.errorRatePct}%` }, // RPC error rate — ~0 = healthy
            { label: ft('m.dataPoints'), value: fmtCompact(s.dbEntries) }, // historian rows (ClickHouse)
          ],
        );
      // HARDWARE — disk + load (utilization) + live process count (activity). No temp dial:
      // a cloud VM exposes no thermal sensor. Memory + disk capacity break down as text.
      case 'hardware':
        return tabBody(
          [
            { value: this.pct(s.diskUsedGb, s.diskTotalGb), label: ft('m.disk') },
            { value: loadPct(s.load1, s.cpuCores), label: ft('server.load') },
            { value: s.processCount || null, label: ft('server.processes'), max: 1000, unit: '', tone: 'activity' },
          ],
          [
            { label: ft('m.memUsed'), value: fmtGb(s.memUsedGb) },
            { label: ft('server.memTotal'), value: fmtGb(s.memTotalGb) },
            { label: ft('m.diskUsed'), value: fmtGb(s.diskUsedGb) },
            { label: ft('m.diskFree'), value: fmtGb(Math.max(0, s.diskTotalGb - s.diskUsedGb)) },
          ],
        );
      // SOFTWARE — egress + total traffic vs the included cap (utilization) + cloud broker
      // throughput (activity). Inbound is unlimited → there is no ingress dial.
      case 'software':
        return tabBody(
          [
            { value: s.egressPct || null, label: ft('m.egress') },
            { value: s.egressPct + s.ingressPct || null, label: ft('m.traffic') },
            { value: s.brokerMsgsPerSec > 0 ? Math.min(100, Math.round(s.brokerMsgsPerSec / 20)) : null, label: ft('m.cloudBroker'), tone: 'activity' },
          ],
          [
            { label: ft('m.ips'), value: s.brokerMsgsPerSec ? String(Math.round(s.brokerMsgsPerSec)) : DASH }, // broker msgs/sec
            { label: ft('server.os'), value: s.os || DASH },
            { label: ft('server.kernel'), value: s.kernel || DASH },
            { label: ft('m.release'), value: DASH }, // wire: stack build version
          ],
        );
    }
  }

  render(): TemplateResult {
    const s = this.server;
    if (!s) return html``;
    return html`
      <div class="panel-backdrop ${this.opened ? 'panel-backdrop--visible' : ''}" @click=${this.close}></div>
      <aside
        class="side-panel-container side-panel-container--overlay side-panel-container--right side-panel-container--${this
          .opened
          ? 'open'
          : 'closed'} fleet-gw-panel"
      >
        <div class="fgp-head">
          <div class="fgp-head-row">
            <vitesse-icon name="server" size="18"></vitesse-icon>
            <div class="fgp-head-main">
              <div class="fgp-head-name-row"><div class="fgp-head-name">${s.name}</div></div>
              <div class="fgp-head-id" title=${s.id}>${s.id}</div>
            </div>
            ${iconButton({ icon: 'x', title: ft('btn.close'), onClick: () => this.close() })}
          </div>
          <div class="fgp-badges">
            ${statusBadge('green', ft('status.online'))} ${statusBadge('blue', ft('legend.cloud'))}
            ${s.geo ? statusBadge('gray', s.geo) : ''}
          </div>
        </div>

        ${s.hasStats
          ? html`
              <div class="fgp-tabs">
                ${pillTabBar({ tabs: this.tabItems, active: this.tab, onChange: (id) => (this.tab = id as Tab) })}
              </div>
              <div class="fgp-body">${this.renderTab(s)}</div>
            `
          : html`<div class="fgp-body"><div class="fsp-restricted">${ft('server.restricted')}</div></div>`}
      </aside>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'fleet-server-panel': FleetServerPanel;
  }
}
